import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Store,
  Tag,
  Truck,
  Wallet,
  BarChart3,
  Users,
  Megaphone,
  Settings,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/atoms/button';
import { ScrollArea } from '@/components/atoms/scroll-area';
import { useUIStore } from '@/lib/stores/ui.store';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/molecules/tooltip';

const navigation = [
  { name: 'dashboard', href: '/', icon: LayoutDashboard },
  { name: 'orders', href: '/orders', icon: ShoppingCart },
  { name: 'messages', href: '/messages', icon: MessageSquare },
  { name: 'products', href: '/products', icon: Package },
  { name: 'shops', href: '/shops', icon: Store },
  { name: 'promotions', href: '/promotions', icon: Tag },
  { name: 'shipping', href: '/shipping', icon: Truck },
  { name: 'wallet', href: '/wallet', icon: Wallet },
  { name: 'metrics', href: '/metrics', icon: BarChart3 },
  { name: 'employees', href: '/employees', icon: Users },
  { name: 'ads', href: '/ads', icon: Megaphone },
];

const bottomNavigation = [
  { name: 'settings', href: '/settings', icon: Settings },
];

export function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  };

  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col bg-sidebar border-r border-sidebar-border transition-all duration-300',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className={cn(
        "flex h-16 items-center border-b border-sidebar-border",
        sidebarCollapsed ? "justify-center px-2" : "justify-between px-4"
      )}>
        {!sidebarCollapsed ? (
          <>
            <Link to="/" className="flex items-center gap-2">
              <img src="/volovolo.png" alt="Volovolo" className="h-8 w-auto" />
              <span className="font-semibold text-lg text-sidebar-foreground">Pro</span>
            </Link>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={toggleSidebar}
              className="text-sidebar-foreground hover:bg-sidebar-accent"
            >
              <ChevronLeft className="size-4" />
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Link to="/">
              <img src="/icon.png" alt="Volovolo" className="h-8 w-8" />
            </Link>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={toggleSidebar}
              className="text-sidebar-foreground hover:bg-sidebar-accent"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="space-y-1 px-2">
          {navigation.map((item) => {
            const active = isActive(item.href);
            const NavLink = (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground',
                  sidebarCollapsed && 'justify-center px-2'
                )}
              >
                <item.icon className="size-5 shrink-0" />
                {!sidebarCollapsed && <span>{t(`navigation.${item.name}`)}</span>}
              </Link>
            );

            if (sidebarCollapsed) {
              return (
                <Tooltip key={item.name}>
                  <TooltipTrigger asChild>{NavLink}</TooltipTrigger>
                  <TooltipContent side="right">{t(`navigation.${item.name}`)}</TooltipContent>
                </Tooltip>
              );
            }

            return NavLink;
          })}
        </nav>
      </ScrollArea>

      {/* Bottom Navigation */}
      <div className="border-t border-sidebar-border p-2">
        {bottomNavigation.map((item) => {
          const active = isActive(item.href);
          const NavLink = (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground',
                sidebarCollapsed && 'justify-center px-2'
              )}
            >
              <item.icon className="size-5 shrink-0" />
              {!sidebarCollapsed && <span>{t(`navigation.${item.name}`)}</span>}
            </Link>
          );

          if (sidebarCollapsed) {
            return (
              <Tooltip key={item.name}>
                <TooltipTrigger asChild>{NavLink}</TooltipTrigger>
                <TooltipContent side="right">{t(`navigation.${item.name}`)}</TooltipContent>
              </Tooltip>
            );
          }

          return NavLink;
        })}
      </div>
    </aside>
  );
}
